<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { Icon } from '@brighterly/lib-lumee-ui'

import { useOptionsStore } from '@stores/options'
import { useLessonRoomStore } from '@stores/lesson-room'
import { useVoipRoomStore } from '@stores/voip-room'

import { useIsPresentingLiveKit } from '@hooks/classroom/presenting-live-kit.hook'
import { useClassroomJoining } from '@hooks/classroom/joining.hook'
import { useConnectionWatchLiveKit } from '@hooks/classroom/connection-watch-live-kit.hook'

import ClassroomHeader from '@Molecules/Classroom/ClassroomHeader.vue'
import ClassroomControlsBar from '@Widgets/Classroom/ClassroomControlsBar.vue'
import LessonRoomGuidesPopup from '@Blocks/LessonRoom/LessonRoomGuidesPopup.vue'
import LessonRoomChat from '@Blocks/LessonRoom/LessonRoomChat.vue'
import LessonRoomSettingsDrawer from '@Blocks/LessonRoom/LessonRoomSettingsDrawer.vue'
import LessonRoomPermissionsBlocked from '@Blocks/LessonRoom/LessonRoomPermissionsBlocked.vue'
import { LoaderBlockDomain } from '@UI/Loaders/LoaderBlockDomain'
import { LoaderFullScreenDomain } from '@UI/Loaders/LoaderFullScreenDomain'

defineOptions({
  name: 'Classroom',
})

const optionsStore = useOptionsStore()
const lessonRoomStore = useLessonRoomStore()
const voipRoomStore = useVoipRoomStore()

const isPresenting = useIsPresentingLiveKit()
const { isJoining } = useClassroomJoining()

useConnectionWatchLiveKit()
const controlsRevealed = ref<boolean>(false)

// A fresh share starts hidden; ending a share restores the normal bar.
watch(isPresenting, (presenting) => {
  if (!presenting) controlsRevealed.value = false
})

const isControlsHidden = computed<boolean>(() => isPresenting.value && !controlsRevealed.value)
const isPermissionsPromptShown = computed<boolean>(() => lessonRoomStore.isPermissionsBlocked && !voipRoomStore.isAlternativeVoip)
const isJoiningLoaderShown = computed<boolean>(() => isJoining.value && !isPermissionsPromptShown.value)

const toggleControls = (): void => {
  controlsRevealed.value = !controlsRevealed.value
}
</script>

<template>
  <div
    v-if="optionsStore.hasData"
    :class="['classroom', {
      'classroom--presenting': isPresenting,
      'classroom--revealed': controlsRevealed,
    }]"
  >
    <ClassroomHeader class="classroom__header" />

    <main class="classroom__content">
      <slot />
    </main>

    <div
      :class="['classroom__controls', {
        'classroom__controls--presenting': isPresenting,
        'classroom__controls--hidden': isControlsHidden,
      }]"
    >
      <button
        v-if="isPresenting"
        class="classroom__controls-toggle"
        type="button"
        :aria-label="controlsRevealed ? 'Hide controls' : 'Show controls'"
        @click="toggleControls"
      >
        <Icon
          :name="controlsRevealed ? 'chevron-down' : 'chevron-up'"
          size="md"
          color="inherit"
        />
      </button>

      <ClassroomControlsBar />
    </div>

    <LoaderFullScreenDomain v-if="isJoiningLoaderShown" />

    <LessonRoomGuidesPopup v-if="lessonRoomStore.isGuideOpen" />
    <LessonRoomChat />
    <LessonRoomSettingsDrawer />
    <LessonRoomPermissionsBlocked v-if="isPermissionsPromptShown" />
  </div>
  <LoaderBlockDomain v-else />
</template>

<style scoped lang="scss" src="@scss/layouts/classroom.scss" />
